/**
 * ChatWidget — floating AI assistant, bottom-right (Forge-style).
 *
 * Fixed FAB opens a slide-up panel that:
 *   - renders assistant answers as formatted markdown (tables, bold, lists,
 *     headings) via a tiny built-in renderer (no dependency, builds React
 *     elements so there's no HTML-injection surface), and
 *   - is drag-resizable from the top-left grip; size persists to localStorage.
 *
 * Every rendered table gets a quiet "Download CSV" affordance, and each
 * assistant message gets a Copy / Download-all-tables action row (see
 * ./chatExport.js — CSV serialising + clipboard + Blob download, no deps).
 *
 * Posts to POST /api/chat (grounded, read-only tools — see server/routes/chat.js).
 * Changes (10/13/26): an answer may carry `proposals` - changes the assistant
 * PROPOSES. Each renders as a ProposalCard; nothing changes until the person
 * presses Confirm, and then THIS browser sends each change through the app's
 * own saves (./proposalActions.js) with the person's own token. What happened
 * goes back to the model as an `outcome` message on the next question.
 * The window.fetch interceptor (src/index.js) attaches the Entra token. Shown to
 * all verified roles behind CHAT_ENABLED in App.jsx — the SERVER walls the data
 * each role may query (engineering side vs staffing side).
 */
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDraggableDialog } from '../../hooks/useDraggableDialog';
import { useApp } from '../../context/AppContext';
import { ProposalCard, initialProposalState } from './ProposalCard';
import { sendJson, VIA, outcomeLabel } from './proposalActions';
import { blocksToPlainText, collectTables, copyText, csvFilename, downloadCsvText, tableToCsv, tablesToCsv } from './chatExport';

const GREEN = '#1B5E20';
const MIN_W = 320,
  MIN_H = 360;
const DEFAULT_SIZE = { w: 400, h: 580 };
export const GREETING =
  'Hi! Ask me about projects, opportunities, customers, engineers, timesheets, or the staffing CRM. I answer from live GlobalOps data, ' +
  'and I can make changes for you (new or duplicated records, status, tasks, notes, accounts and contacts) - I show you each change first, and nothing is saved until you confirm.';

/* ── tiny markdown renderer (subset: tables, headings, bold/italic/code, lists) ── */
const codeStyle = {
  background: '#EDEFEA',
  padding: '1px 5px',
  borderRadius: 4,
  fontFamily: "'JetBrains Mono', Consolas, monospace",
  fontSize: '0.92em',
};

function renderInline(str) {
  const nodes = [];
  let key = 0,
    last = 0,
    m;
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`|\*([^*]+)\*)/g;
  while ((m = re.exec(str))) {
    if (m.index > last) nodes.push(str.slice(last, m.index));
    if (m[2] !== undefined) nodes.push(<strong key={key++}>{m[2]}</strong>);
    else if (m[3] !== undefined)
      nodes.push(
        <code key={key++} style={codeStyle}>
          {m[3]}
        </code>
      );
    else if (m[4] !== undefined) nodes.push(<em key={key++}>{m[4]}</em>);
    last = m.index + m[0].length;
  }
  if (last < str.length) nodes.push(str.slice(last));
  return nodes;
}

const splitRow = (l) =>
  l
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim());
const isSep = (l) => l.includes('-') && /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(l);
const isListItem = (l) => /^\s*([-*]\s+|\d+\.\s+)/.test(l);

function parseBlocks(text) {
  const lines = String(text).replace(/\r/g, '').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    // table
    if (line.includes('|') && i + 1 < lines.length && isSep(lines[i + 1]) && lines[i + 1].includes('|')) {
      const headers = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push({ type: 'table', headers, rows });
      continue;
    }
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) {
      blocks.push({ type: 'heading', level: h[1].length, text: h[2] });
      i++;
      continue;
    }
    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) {
      blocks.push({ type: 'hr' });
      i++;
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }
    const para = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !isListItem(lines[i]) &&
      !/^#{1,3}\s+/.test(lines[i]) &&
      !(lines[i].includes('|') && i + 1 < lines.length && isSep(lines[i + 1]))
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push({ type: 'p', text: para.join(' ') });
  }
  return blocks;
}

const TH = {
  textAlign: 'left',
  padding: '5px 9px',
  background: '#E8EFE0',
  color: '#0D0D0D',
  fontWeight: 800,
  fontSize: 10.5,
  borderBottom: '2px solid ' + GREEN,
  whiteSpace: 'nowrap',
};
const TD = { padding: '5px 9px', borderBottom: '1px solid #EEE', fontSize: 11.5, verticalAlign: 'top' };

/* ── quiet export affordances (module-level components only) ──────────────── */

/** Tiny ghost link — brand green, 10.5px, dims until hovered. */
function GhostAction({ label, title, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: 'none',
        border: 'none',
        padding: '1px 2px',
        margin: 0,
        cursor: 'pointer',
        color: GREEN,
        fontFamily: 'inherit',
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: 0.2,
        lineHeight: 1.4,
        opacity: hover ? 1 : 0.62,
        textDecoration: hover ? 'underline' : 'none',
      }}
    >
      {label}
    </button>
  );
}

/** A rendered markdown table + its own CSV download, top-right. */
function TableCard({ headers, rows, label }) {
  const onDownload = () => downloadCsvText(csvFilename(label), tableToCsv({ headers, rows }));
  return (
    <div style={{ margin: '6px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <GhostAction label="Download CSV" title="Download this table as CSV" onClick={onDownload} />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', background: '#fff', border: '1px solid #E4E7E2', borderRadius: 6 }}>
          <thead>
            <tr>
              {headers.map((hd, j) => (
                <th key={j} style={TH}>
                  {renderInline(hd)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, j) => (
              <tr key={j} style={{ background: j % 2 ? '#FAFBF7' : '#fff' }}>
                {r.map((c, k) => (
                  <td key={k} style={TD}>
                    {renderInline(c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Per-message footer: Copy (plain text) + Download CSV (all tables in it). */
function MessageActions({ text }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const blocks = parseBlocks(text);
  const tables = collectTables(blocks);

  const onCopy = async () => {
    const ok = await copyText(blocksToPlainText(blocks));
    if (!ok) return;
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1500);
  };
  const onDownloadAll = () => {
    const labelled = tables.find((t) => t.label);
    downloadCsvText(csvFilename(labelled ? labelled.label : '', tables.length > 1 ? 'tables' : 'table'), tablesToCsv(tables));
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: 6,
        paddingTop: 4,
        borderTop: '1px solid #E4E7E2',
      }}
    >
      <GhostAction label={copied ? 'Copied' : 'Copy'} title="Copy this answer as plain text" onClick={onCopy} />
      {tables.length > 0 && (
        <GhostAction
          label={tables.length > 1 ? `Download CSV (${tables.length})` : 'Download CSV'}
          title={tables.length > 1 ? `Download all ${tables.length} tables as one CSV` : 'Download the table as CSV'}
          onClick={onDownloadAll}
        />
      )}
    </div>
  );
}

function Markdown({ text }) {
  const blocks = parseBlocks(text);
  const labelAt = {};
  collectTables(blocks).forEach((t) => {
    labelAt[t.index] = t.label;
  });
  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === 'heading') {
          const size = b.level === 1 ? 15 : b.level === 2 ? 13.5 : 12.5;
          return (
            <div key={i} style={{ fontSize: size, fontWeight: 800, color: '#0D0D0D', margin: '8px 0 4px' }}>
              {renderInline(b.text)}
            </div>
          );
        }
        if (b.type === 'hr') return <hr key={i} style={{ border: 0, borderTop: '1px solid #E4E7E2', margin: '8px 0' }} />;
        if (b.type === 'ul')
          return (
            <ul key={i} style={{ margin: '4px 0', paddingLeft: 18 }}>
              {b.items.map((it, j) => (
                <li key={j} style={{ marginBottom: 2 }}>
                  {renderInline(it)}
                </li>
              ))}
            </ul>
          );
        if (b.type === 'ol')
          return (
            <ol key={i} style={{ margin: '4px 0', paddingLeft: 20 }}>
              {b.items.map((it, j) => (
                <li key={j} style={{ marginBottom: 2 }}>
                  {renderInline(it)}
                </li>
              ))}
            </ol>
          );
        if (b.type === 'table') {
          return <TableCard key={i} headers={b.headers} rows={b.rows} label={labelAt[i] || ''} />;
        }
        return (
          <p key={i} style={{ margin: '4px 0' }}>
            {renderInline(b.text)}
          </p>
        );
      })}
    </>
  );
}

const bubbleStyle = (role) => ({
  alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
  maxWidth: role === 'user' ? '85%' : '96%',
  background: role === 'user' ? GREEN : '#F1F3EE',
  color: role === 'user' ? '#fff' : '#0D0D0D',
  borderRadius: role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
  padding: role === 'user' ? '9px 12px' : '10px 13px',
  fontSize: 12.5,
  lineHeight: 1.5,
  wordBreak: 'break-word',
});

export function ChatWidget() {
  const { createProject, saveProject, updateProject, refreshQuietly } = useApp();
  const [open, setOpen] = useState(false);
  const drag = useDraggableDialog(open, { transform: true }); // moved by its green title bar (10/9/26)
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([{ role: 'assistant', content: GREETING }]);
  // each proposal's card state, by proposal id (kept here so closing the panel forgets nothing)
  const [cards, setCards] = useState({});
  const api = useMemo(
    () => ({ createProject, saveProject, request: sendJson, refresh: refreshQuietly }),
    [createProject, saveProject, refreshQuietly]
  );
  const cardState = useCallback(
    (proposal) => (updater) =>
      setCards((m) => {
        const cur = m[proposal.id] || initialProposalState(proposal);
        return { ...m, [proposal.id]: typeof updater === 'function' ? updater(cur) : updater };
      }),
    []
  );
  // what Confirm / Cancel did, for the model to read with the next question
  const addOutcome = useCallback((text) => setMessages((m) => [...m, { role: 'outcome', content: text }]), []);
  const savePo = useCallback((id, patch) => updateProject(id, { ...patch, ...VIA }), [updateProject]);
  const [busy, setBusy] = useState(false);
  const [size, setSize] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('globalops_chat_size'));
      if (s && s.w && s.h) return s;
    } catch {
      /* default */
    }
    return DEFAULT_SIZE;
  });
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Drag-resize from the top-left grip (panel is bottom-right anchored, so it
  // grows toward the top-left). Clamped to the viewport; persisted on release.
  const onResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      const startX = e.clientX,
        startY = e.clientY,
        sw = size.w,
        sh = size.h;
      const onMove = (ev) => {
        const w = Math.min(Math.max(sw + (startX - ev.clientX), MIN_W), window.innerWidth - 32);
        const h = Math.min(Math.max(sh + (startY - ev.clientY), MIN_H), window.innerHeight - 110);
        setSize({ w, h });
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        setSize((s) => {
          try {
            localStorage.setItem('globalops_chat_size', JSON.stringify(s));
          } catch {
            /* ignore */
          }
          return s;
        });
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [size]
  );

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setBusy(true);
    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: next.slice(1).map((m) => ({ role: m.role, content: m.content })) }),
      });
      const data = await res.json().catch(() => ({}));
      const reply = res.ok ? data.reply || 'No answer.' : data.error || `Error (${res.status}).`;
      const proposals = res.ok && Array.isArray(data.proposals) ? data.proposals : [];
      setMessages((m) => [...m, { role: 'assistant', content: reply, proposals }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'Could not reach the assistant. Check your connection and try again.' }]);
    } finally {
      setBusy(false);
    }
  }, [input, busy, messages]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {open && (
        <div
          ref={drag.ref}
          onPointerDown={drag.onPointerDown}
          className="go-pop-in"
          style={{
            ...drag.style,
            position: 'fixed',
            bottom: 88,
            right: 24,
            width: size.w,
            height: size.h,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 110px)',
            background: '#fff',
            borderRadius: 14,
            border: '1px solid #E0E4DC',
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          {/* resize grip (top-left) */}
          <div
            onPointerDown={onResizeStart}
            title="Drag to resize"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: 18,
              height: 18,
              cursor: 'nwse-resize',
              zIndex: 2,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0 45%, transparent 45%)',
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              style={{ position: 'absolute', top: 3, left: 3, opacity: 0.55 }}
              aria-hidden="true"
            >
              <path d="M11 1 L1 11 M7 1 L1 7 M11 5 L5 11" stroke="#1B5E20" strokeWidth="1.2" fill="none" />
            </svg>
          </div>

          <div
            data-dialog-handle=""
            style={{
              padding: '13px 16px',
              background: GREEN,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 800 }}>GlobalOps Assistant</span>
              <span
                style={{
                  fontSize: 8.5,
                  fontWeight: 800,
                  letterSpacing: 0.8,
                  background: 'rgba(255,255,255,0.22)',
                  padding: '2px 6px',
                  borderRadius: 999,
                }}
              >
                BETA
              </span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close assistant"
              style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: 2 }}
            >
              ×
            </button>
          </div>

          <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {messages.map((m, i) =>
              m.role === 'outcome' ? (
                <div
                  key={i}
                  style={{
                    alignSelf: 'center',
                    fontSize: 10.5,
                    color: '#6B7A68',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {outcomeLabel(m.content)}
                </div>
              ) : (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6, alignSelf: 'stretch' }}>
                  <div style={bubbleStyle(m.role)}>
                    {m.role === 'assistant' ? <Markdown text={m.content} /> : m.content}
                    {m.role === 'assistant' && i > 0 && <MessageActions text={m.content} />}
                  </div>
                  {(m.proposals || []).map((p) => (
                    <ProposalCard
                      key={p.id}
                      proposal={p}
                      state={cards[p.id]}
                      onState={cardState(p)}
                      api={api}
                      onOutcome={addOutcome}
                      onSavePo={savePo}
                    />
                  ))}
                </div>
              )
            )}
            {busy && <div style={{ ...bubbleStyle('assistant'), color: '#888' }}>Thinking…</div>}
          </div>

          <div style={{ borderTop: '1px solid #EEE', padding: 10, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Ask about your data, or ask for a change…"
              style={{
                flex: 1,
                resize: 'none',
                border: '1px solid #D8DED4',
                borderRadius: 8,
                padding: '9px 11px',
                fontSize: 12.5,
                fontFamily: 'inherit',
                color: '#0D0D0D',
                maxHeight: 96,
                outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={send}
              disabled={busy || !input.trim()}
              style={{
                background: busy || !input.trim() ? '#A5B8A6' : GREEN,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '9px 14px',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: 'inherit',
                cursor: busy || !input.trim() ? 'default' : 'pointer',
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close assistant' : 'Open assistant'}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: GREEN,
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          zIndex: 1000,
          boxShadow: '0 6px 20px rgba(27,94,32,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {open ? (
          <span style={{ fontSize: 22, lineHeight: 1 }}>×</span>
        ) : (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        )}
      </button>
    </>
  );
}
